import { CartUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'gbSavedCart:v1';
const MAX_ITEMS = 20;

export function readSavedItems(storage = localStorage) {
  const items = JSON.parse(storage.getItem(STORAGE_KEY) || '[]');
  if (!Array.isArray(items) || items.length > MAX_ITEMS || new Set(items.map((item) => item?.id)).size !== items.length || items.some((item) =>
    !item || typeof item.id !== 'string' || !/^\d+$/.test(item.id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 ||
    typeof item.title !== 'string' || typeof item.url !== 'string' ||
    !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9-]+(?:\?variant=\d+)?$/i.test(item.url)
  )) throw new Error('storage_error');
  return items;
}

export function writeSavedItems(items, storage = localStorage) {
  if (items.length > MAX_ITEMS) throw new Error('full');
  const value = JSON.stringify(items);
  try {
    storage.setItem(STORAGE_KEY, value);
    if (storage.getItem(STORAGE_KEY) !== value) throw new Error();
  } catch {
    throw new Error('storage_error');
  }
}

class SavedCart extends HTMLElement {
  #busy = false;
  #uncertain = false;
  #load;
  #listeners;
  #strings;

  connectedCallback() {
    this.#strings = JSON.parse(this.querySelector('[data-saved-strings]').textContent);
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    document.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('storage', this.#onStorage, { signal });
    document.documentElement.classList.add('gb-saved-ready');
    this.#render();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#load?.abort();
    document.documentElement.classList.remove('gb-saved-ready');
  }

  #onStorage = (event) => {
    if (event.key === STORAGE_KEY || event.key === null) this.#render();
  };

  #message(key) {
    this.hidden = false;
    this.querySelector('[data-saved-status]').textContent = this.#strings[key] || this.#strings.cart_error;
  }

  #onClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-gb-save-item], [data-saved-restore], [data-saved-remove]');
    if (!button || button.disabled || this.#busy || this.#uncertain) return;
    if (!button.hasAttribute('data-gb-save-item') && !this.contains(button)) return;
    event.preventDefault();
    const id = button.closest('[data-saved-id]')?.dataset.savedId;
    if (button.hasAttribute('data-saved-remove')) {
      try {
        writeSavedItems(readSavedItems().filter((item) => item.id !== id));
        await this.#render();
        this.#message('removed');
        this.querySelector('h2').focus({ preventScroll: true });
      } catch { this.#message('storage_error'); }
      return;
    }
    if (document.querySelector('.cart-items-disabled')) return;
    this.#busy = true;
    const controls = [...document.querySelectorAll('cart-items-component'), this.querySelector('[data-saved-list]')];
    const inertStates = controls.map((node) => node.inert);
    controls.forEach((node) => { node.inert = true; });
    document.documentElement.classList.add('gb-saved-busy');
    const saving = button.hasAttribute('data-gb-save-item');
    try {
      if (saving) await this.#save(button.dataset.gbSaveItem);
      else await this.#restore(id);
    } catch (error) {
      this.#message(error.message in this.#strings ? error.message : saving ? 'save_error' : 'restore_error');
    } finally {
      controls.forEach((node, index) => { node.inert = inertStates[index]; });
      document.documentElement.classList.remove('gb-saved-busy');
      this.#busy = false;
    }
  };

  async #request(path, body) {
    const root = this.dataset.rootUrl.replace(/\/?$/, '/');
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error('cart_error');
    return data;
  }

  #sectionRequest() {
    return {
      sections: [...new Set([...document.querySelectorAll('cart-items-component')].map((node) => node.dataset.sectionId).filter(Boolean))].slice(0, 5),
      sections_url: window.location.pathname,
    };
  }

  async #save(key) {
    const cart = await this.#request('cart.js');
    const item = cart.items.find((entry) => entry.key === key);
    if (!item || item.selling_plan_allocation || item.parent_relationship || item.item_components?.length ||
      Object.keys(item.properties || {}).length || item.instructions?.can_remove === false) throw new Error('save_error');
    const previous = readSavedItems();
    const existing = previous.find((entry) => entry.id === String(item.variant_id));
    const url = new URL(item.url, window.location.origin);
    url.search = '';
    url.searchParams.set('variant', item.variant_id);
    const saved = {
      id: String(item.variant_id),
      quantity: item.quantity + (existing?.quantity || 0),
      title: item.product_title,
      url: url.pathname + url.search,
    };
    writeSavedItems([saved, ...previous.filter((entry) => entry.id !== saved.id)]);
    let updated;
    try {
      updated = await this.#request('cart/change.js', { id: key, quantity: 0, ...this.#sectionRequest() });
    } catch {
      try {
        updated = await this.#request('cart.js');
      } catch {
        this.#uncertain = true;
        this.#render();
        throw new Error('save_error');
      }
      if (updated.items.some((entry) => entry.key === key)) {
        writeSavedItems(previous);
        this.#render();
        throw new Error('cart_error');
      }
    }
    this.#updateCart(updated);
    this.#render();
    this.#message('saved');
    this.querySelector('h2').focus();
  }

  async #restore(id) {
    const item = readSavedItems().find((entry) => entry.id === id);
    if (!item) return;
    writeSavedItems(readSavedItems());
    const before = await this.#request('cart.js');
    const quantityBefore = this.#variantQuantity(before, id);
    let result;
    try {
      result = await this.#request('cart/add.js', {
        items: [{ id: item.id, quantity: item.quantity }],
        ...this.#sectionRequest(),
      });
    } catch {
      let after;
      try { after = await this.#request('cart.js'); }
      catch {
        this.#uncertain = true;
        throw new Error('restore_error');
      }
      const added = Math.max(0, this.#variantQuantity(after, id) - quantityBefore);
      if (added > 0) {
        const remaining = Math.max(0, item.quantity - added);
        writeSavedItems(readSavedItems().flatMap((entry) => entry.id !== id ? [entry] : remaining ? [{ ...entry, quantity: remaining }] : []));
        this.#updateCart(after);
      }
      throw new Error('restore_error');
    }
    try { writeSavedItems(readSavedItems().filter((entry) => entry.id !== id)); }
    catch {
      this.#uncertain = true;
      this.#updateCart({ sections: result.sections, item_count: before.item_count + item.quantity });
      throw new Error('restore_error');
    }
    const sectionHtml = result.sections?.[this.#sectionRequest().sections[0]];
    const section = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
    const count = Number.parseInt(section.querySelector('[ref="cartItemCount"]')?.textContent, 10);
    this.#updateCart({ item_count: Number.isFinite(count) ? count : before.item_count + item.quantity, sections: result.sections });
    this.#render();
    this.#message('restored');
    this.querySelector('h2').focus({ preventScroll: true });
  }

  #variantQuantity(cart, id) {
    return cart.items.filter((item) => String(item.variant_id) === id && !item.selling_plan_allocation && !Object.keys(item.properties || {}).length)
      .reduce((quantity, item) => quantity + item.quantity, 0);
  }

  #updateCart(cart) {
    const sections = this.#sectionRequest().sections;
    if (sections.some((id) => !cart.sections?.[id])) {
      window.location.reload();
      return;
    }
    this.dispatchEvent(new CartUpdateEvent(cart, 'gb-cart-saved', {
      source: 'gb-cart-saved', itemCount: cart.item_count, sections: cart.sections,
    }));
  }

  async #render() {
    this.#load?.abort();
    this.#load = new AbortController();
    const { signal } = this.#load;
    let items;
    try { items = readSavedItems(); }
    catch { this.#message('storage_error'); return; }
    const list = this.querySelector('[data-saved-list]');
    list.replaceChildren();
    this.hidden = items.length === 0 && !this.querySelector('[data-saved-status]').textContent;
    const cards = items.map((item) => {
      const card = this.querySelector('[data-saved-fallback]').content.firstElementChild.cloneNode(true);
      card.dataset.savedId = item.id;
      card.querySelector('[data-saved-title]').textContent = item.title;
      card.querySelector('[data-saved-title]').href = item.url;
      card.querySelector('[data-saved-quantity]').textContent = this.#strings.quantity.replace('{{ quantity }}', item.quantity);
      list.append(card);
      return card;
    });
    await Promise.all(items.map(async (item, index) => {
      const card = cards[index];
      try {
        const url = new URL(item.url, window.location.origin);
        url.searchParams.set('section_id', 'gb-saved-product');
        const response = await fetch(url, { credentials: 'same-origin', signal, cache: 'no-store' });
        if (response.status === 404) {
          card.querySelector('[data-saved-availability]').textContent = this.#strings.unavailable;
          return;
        }
        if (!response.ok) throw new Error();
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = html.querySelector('[data-saved-product]');
        if (!fresh || fresh.dataset.variantId !== item.id) {
          card.querySelector('[data-saved-availability]').textContent = this.#strings.unavailable;
          return;
        }
        if (signal.aborted) return;
        fresh.dataset.savedId = item.id;
        fresh.querySelector('[data-saved-quantity]').textContent = this.#strings.quantity.replace('{{ quantity }}', item.quantity);
        card.replaceWith(fresh);
      } catch {
        if (!signal.aborted) card.querySelector('[data-saved-availability]').textContent = this.#strings.check_error;
      }
    }));
  }
}

if (!customElements.get('gb-cart-saved')) customElements.define('gb-cart-saved', SavedCart);
